import { Portal } from '@radix-ui/react-portal';
import styles from './crt-filter.module.scss';
import { useZoomNormalization } from '@game-libs/exc-libs/src/components/normalize-zoom/useZoomNormalization';
import { GlobalGameStore } from '@game-libs/global-game-store';

export const CRTFilter = () => {
  const { inverseScale, zoomLevel } = useZoomNormalization();

  const { settings: storeSettings } = GlobalGameStore();
  const { chromaticAberration } = storeSettings.value;
  const chromaticAberrationMultiplier = chromaticAberration / 20;

  /**
   * =====================================================
   * Chromatic aberration (UNCHANGED)
   * =====================================================
   */
  const scaleFactor = inverseScale;

  const redOffset = "-1.8;-1.5;-2;-1.3;-1.8;-1.5"
    .split(';')
    .map(v => (parseFloat(v) * scaleFactor * chromaticAberrationMultiplier).toFixed(2))
    .join(';');

  const blueOffset = "1.5;1;1.7;1.6;1.3;1.8;0.9"
    .split(';')
    .map(v => (parseFloat(v) * scaleFactor * chromaticAberrationMultiplier).toFixed(2))
    .join(';');

  /**
   * =====================================================
   * Scanlines — PERFECTLY EVEN
   * =====================================================
   */
  // const dpr = Math.max(1, Math.round(devicePixelRatio));
  // Lock scanlines to physical pixels (ignore browser zoom)
  // const dpr = Math.max(1, Math.round(devicePixelRatio / zoomLevel));
  const dpr = Math.min(
    4,
    Math.max(1, Math.round(1 / zoomLevel))
  );

  // TRUE device-pixel-aligned period
  const dark = 2 * dpr;
  const gap = 2 * dpr;
  const period = dark + gap;

  // Lighter CRT-like contrast
  const darkAlpha = 0.14;
  const gapAlpha = 0.05;

  return (
    <Portal container={document.getElementById('ui-wrapper')}>
      <div className={styles.crtFilter}>
        {/* Scanlines (SVG – pixel perfect) */}
        <svg
          className={styles.scanlines}
          width="100%"
          height="100%"
          preserveAspectRatio="none"
        >
          <defs>
            <pattern
              id="scanlinePattern"
              patternUnits="userSpaceOnUse"
              width={period}
              height={period}
            >
              <rect
                x="0"
                y="0"
                width="100%"
                height={dark}
                fill={`rgba(0,0,0,${darkAlpha})`}
              />
              <rect
                x="0"
                y={dark}
                width="100%"
                height={gap}
                fill={`rgba(0,0,0,${gapAlpha})`}
              />
            </pattern>
          </defs>

          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="url(#scanlinePattern)"
          />
        </svg>

        <div className={styles.vignette} />
        {/* <div className={styles.screenCurvature} /> */}

        {/* Chromatic aberration */}
        <svg width="0" height="0" aria-hidden="true">
          <filter
            id="chromatic-aberration"
            x="-5%"
            y="-5%"
            width="110%"
            height="110%"
            filterUnits="objectBoundingBox"
            colorInterpolationFilters="sRGB"
          >
            <feColorMatrix
              in="SourceGraphic"
              type="matrix"
              values="
                1 0 0 0 0
                0 0 0 0 0
                0 0 0 0 0
                0 0 0 1 0
              "
              result="r"
            />
            <feOffset in="r" dy="-1" result="rOff">
              <animate
                attributeName="dx"
                dur="900ms"
                repeatCount="indefinite"
                values={redOffset}
              />
            </feOffset>

            <feColorMatrix
              in="SourceGraphic"
              type="matrix"
              values="
                0 0 0 0 0
                0 1 0 0 0
                0 0 0 0 0
                0 0 0 1 0
              "
              result="g"
            />

            <feColorMatrix
              in="SourceGraphic"
              type="matrix"
              values="
                0 0 0 0 0
                0 0 0 0 0
                0 0 1 0 0
                0 0 0 1 0
              "
              result="b"
            />
            <feOffset in="b" dy="1" result="bOff">
              <animate
                attributeName="dx"
                dur="1030ms"
                repeatCount="indefinite"
                values={blueOffset}
              />
            </feOffset>

            <feBlend in="rOff" in2="g" mode="screen" result="rg" />
            <feBlend in="rg" in2="bOff" mode="screen" />
          </filter>
        </svg>
      </div>
    </Portal>
  );
};
